export type ExternalToolHelpTopic = 'postgres' | 'mysql';

export type ExternalToolHelpRequest = {
  topic: ExternalToolHelpTopic;
  missingTools?: string[];
};

const MISSING_TOOL_HINTS = [
  'not found',
  'enoent',
  'no such file or directory',
  'is not recognized as an internal or external command',
  'cannot find',
  'could not find',
] as const;

const POSTGRES_BINARIES = ['pg_dump', 'pg_restore', 'psql'] as const;
const MYSQL_BINARIES = ['mysqldump', 'mysql'] as const;

function normalizeText(text: string): string {
  return text.trim().toLowerCase();
}

function includesAny(haystack: string, needles: readonly string[]): boolean {
  return needles.some((needle) => haystack.includes(needle));
}

function detectMentionedTools(text: string, tools: readonly string[]): string[] {
  const normalized = normalizeText(text);
  return tools.filter((tool) => normalized.includes(tool.toLowerCase()));
}

export function inferExternalToolHelpFromText(text: string): ExternalToolHelpRequest | null {
  const normalized = normalizeText(text);
  if (!normalized) return null;

  const looksLikeMissingTool = includesAny(normalized, MISSING_TOOL_HINTS);
  if (!looksLikeMissingTool) return null;

  const mentionedPostgres = detectMentionedTools(normalized, POSTGRES_BINARIES);
  if (mentionedPostgres.length > 0) {
    return { topic: 'postgres', missingTools: mentionedPostgres };
  }

  const mentionedMysql = detectMentionedTools(normalized, MYSQL_BINARIES);
  if (mentionedMysql.length > 0) {
    return { topic: 'mysql', missingTools: mentionedMysql };
  }

  return null;
}

export function getExternalToolHelpBasics(topic: ExternalToolHelpTopic): {
  title: string;
  requiredBinaries: string[];
  links: Array<{ label: string; url: string }>;
  notes: string[];
  installCommands: {
    macos: string[];
    windows: string[];
    linux: string[];
  };
  verifyCommands: string[];
  afterInstallSteps: string[];
} {
  if (topic === 'postgres') {
    return {
      title: 'Install PostgreSQL client tools',
      requiredBinaries: [...POSTGRES_BINARIES],
      links: [
        { label: 'PostgreSQL Downloads', url: 'https://www.postgresql.org/download/' },
        { label: 'Homebrew (macOS)', url: 'https://brew.sh/' },
      ],
      notes: [
        'Jam SQL Studio runs these tools on the same machine where the app is running (your computer), not on the database server.',
        'If the tools are installed but Jam SQL Studio can’t find them, set the bin directory in Settings → Advanced → PostgreSQL Tools.',
      ],
      installCommands: {
        macos: [
          'brew install libpq',
          'echo \'export PATH="$(brew --prefix libpq)/bin:$PATH"\' >> ~/.zshrc',
        ],
        windows: [
          'winget install -e --id PostgreSQL.PostgreSQL',
          '# Ensure the PostgreSQL \\bin folder is on PATH (or configure it in Jam SQL Studio settings).',
        ],
        linux: [
          'sudo apt-get update',
          'sudo apt-get install -y postgresql-client',
        ],
      },
      verifyCommands: [
        'pg_dump --version',
        'pg_restore --version',
        'psql --version',
      ],
      afterInstallSteps: [
        'Restart Jam SQL Studio.',
        'If it still shows missing tools, open Settings → Advanced → PostgreSQL Tools and set the PostgreSQL bin directory.',
      ],
    };
  }

  return {
    title: 'Install MySQL client tools',
    requiredBinaries: [...MYSQL_BINARIES],
    links: [
      { label: 'MySQL Downloads', url: 'https://dev.mysql.com/downloads/' },
      { label: 'Homebrew (macOS)', url: 'https://brew.sh/' },
    ],
    notes: [
      'Jam SQL Studio runs these tools on the same machine where the app is running (your computer), not on the database server.',
      'After installing, make sure the tools are available on your PATH and restart Jam SQL Studio.',
    ],
    installCommands: {
      macos: [
        'brew install mysql-client',
        'echo \'export PATH="$(brew --prefix mysql-client)/bin:$PATH"\' >> ~/.zshrc',
      ],
      windows: [
        'winget install -e --id Oracle.MySQL',
        '# Ensure mysql/mysqldump are on PATH after installation.',
      ],
      linux: [
        'sudo apt-get update',
        'sudo apt-get install -y mysql-client',
      ],
    },
    verifyCommands: ['mysqldump --version', 'mysql --version'],
    afterInstallSteps: ['Restart Jam SQL Studio and try again.'],
  };
}
